import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import PageShell from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { corporateInput, EmptyState, fmtDate } from "@/components/Shared";
import {
  Plus, Briefcase, Phone, CheckCircle, Lightning, XCircle,
  ArrowRight, User, UserCheck, Plug,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const blank = { name: "", email: "", phone: "", address: "", estimated_fee: 0, notes: "", source: "manual", status: "new" };

const STAGES = [
  { key: "assigned",   label: "Asignado",   color: "bg-blue-500",    light: "bg-blue-50 text-blue-700 border-blue-200" },
  { key: "contacted",  label: "Contactado", color: "bg-indigo-500",  light: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  { key: "qualified",  label: "Calificado", color: "bg-violet-500",  light: "bg-violet-50 text-violet-700 border-violet-200" },
  { key: "activating", label: "Activando",  color: "bg-amber-500",   light: "bg-amber-50 text-amber-700 border-amber-200" },
  { key: "converted",  label: "Activado",   color: "bg-emerald-500", light: "bg-emerald-50 text-emerald-700 border-emerald-200" },
];

function StageBadge({ status }) {
  if (status === "lost") {
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border bg-slate-100 text-slate-600 border-slate-200"><XCircle size={10} weight="fill" /> Perdido</span>;
  }
  if (status === "new") {
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border bg-slate-100 text-slate-600 border-slate-200">Nuevo</span>;
  }
  const st = STAGES.find(s => s.key === status);
  if (!st) return <span className="text-xs text-slate-400">{status}</span>;
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${st.light}`}>{st.label}</span>;
}

function MiniPipeline({ status }) {
  const currentIdx = STAGES.findIndex(s => s.key === status);
  if (status === "lost") {
    return <div className="text-xs text-slate-400 italic">Pipeline cerrado</div>;
  }
  return (
    <div className="flex items-center gap-0.5">
      {STAGES.map((s, idx) => {
        const reached = idx <= currentIdx;
        return (
          <div key={s.key} className="flex items-center">
            <div
              title={s.label}
              className={`w-2 h-2 rounded-full transition-all ${reached ? s.color : "bg-slate-200"} ${idx === currentIdx ? "ring-2 ring-offset-1 ring-amber-300" : ""}`}
            />
            {idx < STAGES.length - 1 && (
              <div className={`w-3 h-px ${idx < currentIdx ? s.color.replace("bg-", "bg-") : "bg-slate-200"}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function LeadsAsignadosPage() {
  const params = useParams();
  // path is /support/leads OR /support/leads/lost
  const isLostView = params.filter === "lost";

  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState(isLostView ? "lost" : "active");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    const { data } = await api.get("/support/leads", { params: { status: filter } });
    setLeads(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);
  useEffect(() => { setFilter(isLostView ? "lost" : "active"); }, [isLostView]);

  const save = async () => {
    try {
      await api.post("/support/leads", { ...form, estimated_fee: Number(form.estimated_fee || 0) });
      toast.success("Lead creado");
      setOpen(false); setForm(blank); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const transition = async (id, action, confirmMsg, successMsg, payload = {}) => {
    if (confirmMsg && !confirm(confirmMsg)) return;
    setBusyId(id);
    try {
      await api.post(`/support/leads/${id}/${action}`, payload);
      toast.success(successMsg);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusyId(null); }
  };

  const remove = async (id) => {
    if (!confirm("¿Borrar este lead?")) return;
    try { await api.delete(`/support/leads/${id}`); toast.success("Lead eliminado"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const counts = leads.reduce((a, l) => { a[l.status] = (a[l.status] || 0) + 1; return a; }, {});

  return (
    <PageShell
      section="Support"
      page={isLostView ? "Leads perdidos" : "Leads asignados"}
      context={`${leads.length} en vista`}
      testid="leads-toolbar"
      actions={!isLostView && (
        <Button onClick={() => setOpen(true)} className="bg-slate-900 hover:bg-slate-800 text-white h-9" data-testid="add-lead">
          <Plus size={15} className="mr-1.5" /> Nuevo lead
        </Button>
      )}
    >
      {!isLostView && (
        <div className="mb-5 flex items-center gap-1 bg-white border border-slate-200 rounded-md p-1 w-fit">
          {[
            { v: "active",     label: "En pipeline" },
            { v: "assigned",   label: `Asignados (${counts.assigned || 0})` },
            { v: "contacted",  label: `Contactados (${counts.contacted || 0})` },
            { v: "qualified",  label: `Calificados (${counts.qualified || 0})` },
            { v: "converted",  label: "Activados" },
            { v: "all",        label: "Todos" },
          ].map((f) => (
            <button
              key={f.v}
              onClick={() => setFilter(f.v)}
              data-testid={`lfilter-${f.v}`}
              className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${filter === f.v ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left">
              <th className="px-5 py-3 text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500">Lead</th>
              <th className="px-5 py-3 text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500">Contacto</th>
              <th className="px-5 py-3 text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500">Pipeline</th>
              <th className="px-5 py-3 text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500">Estado</th>
              <th className="px-5 py-3 text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500">Asignado a</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {leads.length === 0 && (
              <tr><td colSpan={6}>
                <EmptyState
                  icon={Briefcase}
                  title={isLostView ? "Sin leads perdidos" : "Sin leads activos"}
                  message={isLostView ? "Aún no se han marcado leads como perdidos." : "Aprueba una orden o crea un lead manual para comenzar."}
                />
              </td></tr>
            )}
            {leads.map((l) => (
              <tr key={l.id} className="hover:bg-slate-50 transition-colors" data-testid={`lead-row-${l.id}`}>
                <td className="px-5 py-3 font-medium text-slate-900">
                  {l.name}
                  {l.source === "order_approved" && <span className="ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-200">de orden</span>}
                </td>
                <td className="px-5 py-3 text-slate-600 text-xs">
                  <div>{l.email || "—"}</div>
                  <div>{l.phone}</div>
                </td>
                <td className="px-5 py-3"><MiniPipeline status={l.status} /></td>
                <td className="px-5 py-3"><StageBadge status={l.status} /></td>
                <td className="px-5 py-3 text-slate-500 text-xs">{l.assigned_agent_email || "—"}</td>
                <td className="px-5 py-3 text-right whitespace-nowrap">
                  <div className="flex items-center justify-end gap-1">
                    {l.status === "assigned" && (
                      <button onClick={() => transition(l.id, "contact", null, "Marcado como contactado")} disabled={busyId === l.id}
                        className="text-indigo-600 hover:bg-indigo-50 px-2 py-1 rounded text-xs font-semibold flex items-center gap-1 disabled:opacity-50" data-testid={`l-contact-${l.id}`}>
                        <Phone size={13} weight="bold" /> Contactar
                      </button>
                    )}
                    {l.status === "contacted" && (
                      <button onClick={() => transition(l.id, "qualify", null, "Lead calificado")} disabled={busyId === l.id}
                        className="text-violet-600 hover:bg-violet-50 px-2 py-1 rounded text-xs font-semibold flex items-center gap-1 disabled:opacity-50" data-testid={`l-qualify-${l.id}`}>
                        <UserCheck size={13} weight="bold" /> Calificar
                      </button>
                    )}
                    {["assigned", "contacted", "qualified"].includes(l.status) && (
                      <button onClick={() => transition(l.id, "activate", "¿Activar el lead? Se creará un cliente activo.", "Cliente creado · pasó a Activos")} disabled={busyId === l.id}
                        className="text-emerald-600 hover:bg-emerald-50 px-2 py-1 rounded text-xs font-semibold flex items-center gap-1 disabled:opacity-50" data-testid={`l-activate-${l.id}`}>
                        <Plug size={13} weight="bold" /> Activar
                      </button>
                    )}
                    {!["lost", "converted"].includes(l.status) && (
                      <button onClick={() => transition(l.id, "lost", "¿Marcar como perdido?", "Lead marcado como perdido", { reason: "no_interest" })} disabled={busyId === l.id}
                        className="text-slate-600 hover:bg-slate-100 px-2 py-1 rounded text-xs font-semibold flex items-center gap-1 disabled:opacity-50" data-testid={`l-lost-${l.id}`}>
                        <XCircle size={13} weight="bold" /> Perdido
                      </button>
                    )}
                    {(l.status === "lost" || l.status === "new") && (
                      <button onClick={() => remove(l.id)} className="text-slate-400 hover:text-red-600 hover:bg-red-50 p-1.5 rounded" title="Borrar" data-testid={`l-del-${l.id}`}>
                        <XCircle size={14} weight="bold" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Nuevo lead</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <div className="col-span-2 space-y-1.5"><Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Nombre *</Label><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className={corporateInput} data-testid="lead-name" /></div>
            <div className="space-y-1.5"><Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className={corporateInput} /></div>
            <div className="space-y-1.5"><Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Teléfono</Label><Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className={corporateInput} /></div>
            <div className="col-span-2 space-y-1.5"><Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Dirección</Label><Input value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} className={corporateInput} /></div>
            <div className="space-y-1.5"><Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Fuente</Label><Input value={form.source} onChange={(e) => setForm({...form, source: e.target.value})} className={corporateInput} /></div>
            <div className="col-span-2 space-y-1.5"><Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Notas</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} className="border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button><Button onClick={save} disabled={!form.name} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="save-lead">Crear lead</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}
